// Admin Learn Types — /api/admin/learn (ff-api admin blueprint)

export type LearnIcon =
  | 'compass'
  | 'shopping-bag'
  | 'tag'
  | 'home'
  | 'gift'
  | 'store'
  | 'badge-check'
  | 'user-round';

export const LEARN_ICON_OPTIONS: { value: LearnIcon; label: string }[] = [
  { value: 'compass', label: 'Compass (Default)' },
  { value: 'shopping-bag', label: 'Shopping Bag' },
  { value: 'tag', label: 'Tag' },
  { value: 'home', label: 'Home' },
  { value: 'gift', label: 'Gift' },
  { value: 'store', label: 'Store' },
  { value: 'badge-check', label: 'Badge Check' },
  { value: 'user-round', label: 'User Round' },
];

export type LearnKind = 'open' | 'monitored';
export type LearnSurface = 'reference' | 'path';

export interface LearnSectionSummary {
  slug: string;
  title: string;
  summary?: string;
  minutes?: number;
  sort_order: number;
  published?: boolean;
}

export interface LearnTopic {
  id: string;
  title: string;
  description?: string;
  icon: LearnIcon | string;
  sort_order: number;
  kind: LearnKind;
  surface: LearnSurface;
  section_count?: number;
  published?: boolean;
  sections?: LearnSectionSummary[];
}

export interface LearnAction {
  label: string;
  href: string;
}

export type LearnBlockItem =
  | { type: 'p'; text: string }
  | { type: 'ul' | 'ol'; items: string[] }
  | { type: 'callout'; tone: 'tip' | 'warning' | 'note'; text: string }
  | { type: 'image'; src: string; alt: string; caption?: string };

export interface LearnBlockGroup {
  id: string;
  heading?: string;
  blocks: LearnBlockItem[];
}

export interface LearnSection {
  slug: string;
  canonical_slug?: string;
  title: string;
  summary?: string;
  minutes?: number;
  updated_at?: string;
  topic: {
    id: string;
    title: string;
  };
  actions?: LearnAction[];
  blocks?: LearnBlockGroup[];
  prev?: { slug: string; title: string } | null;
  next?: { slug: string; title: string } | null;
  markdown?: string;
  published?: boolean;
  sort_order: number;
  surface?: LearnSurface;
  legal?: LearnLegal | null;
}

export type LearnLegalStatus = 'not_sent' | 'in_review' | 'changes_requested' | 'cleared';

/** Policy-vocabulary flag on a section. Only `cleared` unblocks publishing. */
export interface LearnLegal {
  required: boolean;
  status: LearnLegalStatus;
  triggers?: string[];
  notes?: string | null;
  reviewer?: string | null;
  reviewed_at?: string | null;
  blocks_publish?: boolean;
}

export interface LearnImageUploadResponse {
  url: string;
  asid?: number;
}

export interface CreateLearnTopicPayload {
  id?: string;
  title: string;
  description?: string;
  icon?: LearnIcon | string;
  kind?: LearnKind;
  surface?: LearnSurface;
  sort_order?: number;
  published?: boolean;
}

export interface UpdateLearnTopicPayload {
  id: string;
  title?: string;
  description?: string;
  icon?: LearnIcon | string;
  kind?: LearnKind;
  surface?: LearnSurface;
  sort_order?: number;
  published?: boolean;
}

export interface CreateLearnSectionPayload {
  topic: string;
  title: string;
  slug?: string;
  summary?: string;
  markdown?: string;
  actions?: LearnAction[];
  minutes?: number;
  sort_order?: number;
  published?: boolean;
  surface?: LearnSurface;
}

export interface UpdateLearnSectionPayload {
  slug: string;
  topic?: string;
  title?: string;
  new_slug?: string;
  summary?: string;
  markdown?: string;
  actions?: LearnAction[];
  minutes?: number;
  sort_order?: number;
  published?: boolean;
  surface?: LearnSurface;
}

export interface MarkdownPreviewResponse {
  blocks: LearnBlockGroup[];
}

export interface SchemaEnsureResponse {
  success: boolean;
  content_types_created?: number;
  fields_created?: number;
  message?: string;
  [key: string]: unknown;
}

export interface SeedCatalogResponse {
  success: boolean;
  topics_created?: number;
  sections_created?: number;
  skipped?: number;
  message?: string;
  [key: string]: unknown;
}
